import * as React from 'react';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { generatePath, useRouteMatch } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import { PrivateRoute } from '~/components/Auth';
import {
  useEmployeeIdAsSelectedEmployeeId,
  useSelectedEmployeeContext,
} from '~/components/SelectedEmployee';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { usePrevious } from '~/hooks/usePrevious';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { EmployeeViews } from '~/scenes/PortalViews/Views/EmployeeViews';
import { EmployeeModuleTabContext } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/context';
import { EmployeeDevelopmentModule } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/DevelopmentModule';
import { EmployeeModuleRouting_Development } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/DevelopmentModule/data';
import { EmployeePeopleModule } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PeopleModule';
import { EmployeeModuleRouting_People } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PeopleModule/data';
import { EmployeePerformanceModule } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PerformanceModule';
import { EmployeeModuleRouting_Performance } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PerformanceModule/data';
import { EmployeeRewardingModule } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule';
import { EmployeeModuleRouting_Rewarding } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule/data';
import { NewEmployeeRouter } from '~/scenes/PortalViews/Views/EmployeeViews/NewEmployeeView';

type SyncPropsType = {
  employeeId: number;
  children?: ReactNode;
};

const SyncSelectedEmployee = (props: SyncPropsType) => {
  const { employeeId, children } = props;
  const { isOutOfSync } = useEmployeeIdAsSelectedEmployeeId({ employeeId });

  if (isOutOfSync) {
    return <LoadingView />;
  }

  return children ? <>{children}</> : <></>;
};

export const EmployeeRouter = () => {
  const { path, params } = useRouteMatch();
  const { selectedEmployeeId, isLoading } = useSelectedEmployeeContext();

  const defaultPath = `${generatePath(path, params)}/${selectedEmployeeId}`;

  const [activeTab, setActiveTab] = useState<string | undefined>();
  const previousTab = usePrevious(activeTab);

  // NOTE: Don't render view when SelectedEmployeeId is loaded from local storage.
  // Prevents SelectedEmployeeId being reset to LoggedInUserId (default).
  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <EmployeeModuleTabContext.Provider
      value={{ activeTab, previousTab, setActiveTab }}
    >
      <Switch>
        <PrivateRoute
          path={`${path}/:employeeId`}
          render={(renderProps) => {
            const { match } = renderProps;
            const employeeId = parseInt(match.params.employeeId ?? '');

            if (isNaN(employeeId)) {
              if (match.params.employeeId === 'new') {
                return <NewEmployeeRouter />;
              }

              return <Redirect to={defaultPath} />;
            }

            return (
              <SyncSelectedEmployee employeeId={employeeId}>
                <EmployeeViews />
              </SyncSelectedEmployee>
            );
          }}
        />
        <PrivateRoute
          exact
          path={path}
          render={() => <Redirect to={defaultPath} />}
        />
      </Switch>
    </EmployeeModuleTabContext.Provider>
  );
};

export enum EmployeeModulePath {
  People = 'people',
  Rewarding = 'rewarding',
  Performance = 'performance',
  Development = 'development',
}

type EmployeeModuleRouterProps = {
  availableModules: EmployeeModulePath[];
  hasFullAccess: boolean;
};

export const EmployeeModuleRouter = (props: EmployeeModuleRouterProps) => {
  const { availableModules, hasFullAccess } = props;

  const { path, params } = useRouteMatch<{ employeeId: string }>();
  const rootPath = useMemo(() => generatePath(path, params), [path, params]);

  const { defaultModule, defaultPath } = useMemo(() => {
    const defaultModule =
      availableModules.length > 0
        ? availableModules[0]
        : EmployeeModulePath.People;

    const defaultPath = `${rootPath}/${defaultModule}`;

    return {
      defaultModule,
      defaultPath,
    };
  }, [availableModules, rootPath]);

  const getModulePath = useCallback(
    (module: string) => `${rootPath}/${module}`,
    [rootPath],
  );

  const isAccessibleModule = useCallback(
    (selectedModule: string) =>
      availableModules.map((m) => m.toString()).includes(selectedModule) ||
      selectedModule === defaultModule,
    [availableModules, defaultModule],
  );

  return (
    <Switch>
      <Route
        path={`${path}/:module`}
        render={(renderProps) => {
          const { match } = renderProps;

          const selectedModule = match.params.module;

          if (!!selectedModule && isAccessibleModule(selectedModule)) {
            return (
              <Switch>
                <Route
                  path={getModulePath(EmployeeModuleRouting_People.path)}
                  component={() => (
                    <EmployeePeopleModule
                      hasFullAccess={hasFullAccess}
                      modulePath={getModulePath(
                        EmployeeModuleRouting_People.path,
                      )}
                    />
                  )}
                />
                <PrivateRoute
                  path={getModulePath(EmployeeModuleRouting_Rewarding.path)}
                  component={() => (
                    <EmployeeRewardingModule
                      hasFullAccess={hasFullAccess}
                      modulePath={getModulePath(
                        EmployeeModuleRouting_Rewarding.path,
                      )}
                    />
                  )}
                />
                <PrivateRoute
                  path={getModulePath(EmployeeModuleRouting_Performance.path)}
                  component={EmployeePerformanceModule}
                />
                <PrivateRoute
                  path={getModulePath(EmployeeModuleRouting_Development.path)}
                  component={EmployeeDevelopmentModule}
                />
              </Switch>
            );
          }

          return <Redirect to={defaultPath} />;
        }}
      />
      <Route exact path={path} render={() => <Redirect to={defaultPath} />} />
    </Switch>
  );
};

export const EmployeeModulesOldRouteRedirect = (props: {
  modulePath: string;
  defaultTabPath: string;
}) => {
  const { modulePath, defaultTabPath } = props;

  const match = useRouteMatch<{ employeeId?: string; tab?: string }>({
    path: `/${modulePath}/:employeeId/:tab`,
  });

  const { selectedEmployeeId } = useSelectedEmployeeId();

  const newPath = generatePath(
    `/portal/employee/:employeeId/${modulePath}/:tab`,
    {
      employeeId: match?.params?.employeeId ?? selectedEmployeeId,
      tab: match?.params?.tab ?? defaultTabPath,
    },
  );

  return <Redirect to={newPath} />;
};
